// Excel/CSV import + template generation for Room Details.
// Accepts headers in any order. Latent is always derived as Total − Sensible.

import * as XLSX from "xlsx";
import { ALL_MODELS } from "./wizardCalc";

// Header alias map — accepts common header variants from typical Excel spreadsheets.
// Lowercased for matching. Maps to internal field key.
const HEADER_ALIASES = {
  // Room name
  "room name": "name",
  "room": "name",
  "name": "name",
  "space": "name",
  "space name": "name",
  // Total cooling
  "total cooling": "cooling_total_btuh",
  "total cooling (btu/h)": "cooling_total_btuh",
  "total cooling btu/h": "cooling_total_btuh",
  "total cooling btuh": "cooling_total_btuh",
  "total": "cooling_total_btuh",
  "cooling total": "cooling_total_btuh",
  "cooling (btu/h)": "cooling_total_btuh",
  "cooling capacity": "cooling_total_btuh",
  // Sensible
  "sensible": "cooling_sensible_btuh",
  "sensible cooling": "cooling_sensible_btuh",
  "sensible cooling (btu/h)": "cooling_sensible_btuh",
  "sensible (btu/h)": "cooling_sensible_btuh",
  "sensible capacity": "cooling_sensible_btuh",
  // Heating
  "heating": "heating_btuh",
  "heating (btu/h)": "heating_btuh",
  "heating btu/h": "heating_btuh",
  "heating capacity": "heating_btuh",
  // SI capacity aliases
  "total (w)": "cooling_total_btuh",
  "total cooling (w)": "cooling_total_btuh",
  "sensible (w)": "cooling_sensible_btuh",
  "sensible cooling (w)": "cooling_sensible_btuh",
  "heating (w)": "heating_btuh",
  "total (kw)": "cooling_total_btuh",
  "total cooling (kw)": "cooling_total_btuh",
  "sensible (kw)": "cooling_sensible_btuh",
  "sensible cooling (kw)": "cooling_sensible_btuh",
  "heating (kw)": "heating_btuh",
  // OA
  "oa": "oa_flow_cfm",
  "oa flow": "oa_flow_cfm",
  "oa flow (cfm)": "oa_flow_cfm",
  "oa cfm": "oa_flow_cfm",
  "oa (cfm)": "oa_flow_cfm",
  "outside air": "oa_flow_cfm",
  "outdoor air": "oa_flow_cfm",
  "fresh air": "oa_flow_cfm",
  // SI OA aliases
  "oa flow (l/s)": "oa_flow_cfm",
  "oa (l/s)": "oa_flow_cfm",
  // EA
  "ea": "ea_flow_cfm",
  "ea flow": "ea_flow_cfm",
  "ea flow (cfm)": "ea_flow_cfm",
  "ea cfm": "ea_flow_cfm",
  "ea (cfm)": "ea_flow_cfm",
  "exhaust air": "ea_flow_cfm",
  "exhaust": "ea_flow_cfm",
  // SI EA aliases
  "ea flow (l/s)": "ea_flow_cfm",
  "ea (l/s)": "ea_flow_cfm",
  // Quantity
  "qty": "qty",
  "quantity": "qty",
  "count": "qty",
  "no": "qty",
  "no.": "qty",
  "num": "qty",
  "units": "qty",
  // Unit model
  "unit model": "unit_model",
  "model": "unit_model",
  "unit": "unit_model",
  "unit type": "unit_model",
};

const REQUIRED_KEYS = ["name", "cooling_total_btuh", "unit_model"];

function normalizeHeader(h) {
  return String(h || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function fuzzyMatchModel(input) {
  if (!input) return "";
  const s = String(input).trim();
  // Exact match first
  if (ALL_MODELS.includes(s)) return s;
  // Case-insensitive
  const ci = ALL_MODELS.find((m) => m.toLowerCase() === s.toLowerCase());
  if (ci) return ci;
  // Strip "Model " prefix and try again
  const stripped = s.replace(/^model\s+/i, "");
  const ci2 = ALL_MODELS.find(
    (m) => m.toLowerCase() === `model ${stripped}`.toLowerCase()
  );
  if (ci2) return ci2;
  // Last resort: first model whose suffix matches (e.g. "N-f" -> "Model N-f")
  const ci3 = ALL_MODELS.find(
    (m) =>
      m.toLowerCase().endsWith(stripped.toLowerCase()) ||
      m.toLowerCase().includes(stripped.toLowerCase())
  );
  return ci3 || s; // return original if no match — UI will show as invalid
}

// Parse File (xlsx/xls/csv) → array of room objects matching the wizard schema.
// Returns { rooms, errors, warnings }
export async function parseRoomsFile(file, { isSI = false } = {}) {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  if (!sheet) {
    return {
      rooms: [],
      errors: ["Workbook has no sheets"],
      warnings: [],
    };
  }

  // Use header:1 to get raw arrays so we can match header row ourselves
  const rows = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    blankrows: false,
    defval: "",
  });
  if (rows.length < 2) {
    return {
      rooms: [],
      errors: [
        "File must have a header row + at least one data row. " +
          "Tip: download the template using the 'Template' button.",
      ],
      warnings: [],
    };
  }

  const rawHeaders = rows[0];
  // Map column index → field key, AND detect unit system from header text.
  // Headers like "Total (W)" / "OA (L/s)" force SI for that column regardless
  // of the global isSI flag. "kW" headers trigger kW→W conversion.
  const colMap = {};
  const colUnit = {};       // idx → "ip" | "si"
  const colUnitScale = {};  // idx → multiplier (for kW → W)
  rawHeaders.forEach((h, idx) => {
    const norm = normalizeHeader(h);
    const key = HEADER_ALIASES[norm];
    if (!key) return;
    colMap[idx] = key;
    // Detect explicit SI markers in the header
    let unit = isSI ? "si" : "ip";
    let scale = 1;
    if (/\(w\)|\(l\/s\)|\(°c\)|\(m\)/.test(norm)) unit = "si";
    if (/\(kw\)/.test(norm)) {
      unit = "si";
      scale = 1000; // kW → W before applying SI→IP conversion
    }
    if (/\(btu\/h\)|\(cfm\)|\(°f\)|\(ft\)/.test(norm)) unit = "ip";
    colUnit[idx] = unit;
    colUnitScale[idx] = scale;
  });

  // Verify required columns are present
  const presentKeys = Object.values(colMap);
  const missing = REQUIRED_KEYS.filter((k) => !presentKeys.includes(k));
  if (missing.length > 0) {
    const labels = {
      name: "Room Name",
      cooling_total_btuh: "Total Cooling",
      unit_model: "Unit Model",
    };
    return {
      rooms: [],
      errors: [
        `Missing required column(s): ${missing.map((m) => labels[m]).join(", ")}. ` +
          `Detected headers: ${rawHeaders.join(", ")}.`,
      ],
      warnings: [],
    };
  }

  const rooms = [];
  const warnings = [];
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    if (!row || row.every((c) => c === "" || c === null || c === undefined)) {
      continue; // skip blank rows
    }

    const obj = {
      id:
        crypto.randomUUID
          ? crypto.randomUUID()
          : `r_${Date.now()}_${r}_${Math.random()}`,
      name: "",
      qty: 1,
      cooling_total_btuh: "",
      cooling_sensible_btuh: "",
      cooling_latent_btuh: "",
      heating_btuh: "",
      oa_flow_cfm: "",
      ea_flow_cfm: "",
      unit_model: "",
    };
    for (const [idx, key] of Object.entries(colMap)) {
      const val = row[Number(idx)];
      if (val === "" || val === null || val === undefined) continue;
      if (key === "unit_model") {
        const matched = fuzzyMatchModel(val);
        if (!ALL_MODELS.includes(matched)) {
          warnings.push(
            `Row ${r + 1}: Unit Model "${val}" doesn't match any known model. ` +
              "You can fix it in the table after import."
          );
        }
        obj[key] = matched;
      } else if (key === "name") {
        obj[key] = String(val);
      } else if (key === "qty") {
        const parsed = parseInt(String(val), 10);
        obj[key] = !isNaN(parsed) && parsed >= 1 ? parsed : 1;
      } else {
        // Numeric — strip commas, units suffixes; apply SI→IP conversion
        // when the column was flagged as SI.
        let num = parseFloat(String(val).replace(/[^\d.\-]/g, ""));
        if (isNaN(num)) {
          obj[key] = "";
          continue;
        }
        // kW → W (before SI→IP scaling)
        num *= colUnitScale[Number(idx)] || 1;
        if (colUnit[Number(idx)] === "si") {
          // capacity: W → BTU/h, flow: L/s → CFM
          if (key === "cooling_total_btuh" || key === "cooling_sensible_btuh" || key === "heating_btuh") {
            num = num / 0.29307107;
          } else if (key === "oa_flow_cfm" || key === "ea_flow_cfm") {
            num = num / 0.4719474;
          }
        }
        obj[key] = String(Math.round(num * 100) / 100);
      }
    }

    // Derive latent (always Total − Sensible if both available)
    const total = parseFloat(obj.cooling_total_btuh);
    const sens = parseFloat(obj.cooling_sensible_btuh);
    if (!isNaN(total) && !isNaN(sens) && total >= sens) {
      obj.cooling_latent_btuh = (total - sens).toFixed(0);
    }

    // EA is only meaningful for Model N / N-f — clear for other models.
    if (obj.unit_model && obj.unit_model !== "Model N" && obj.unit_model !== "Model N-f") {
      obj.ea_flow_cfm = "";
    }

    if (!obj.name) obj.name = `Room ${rooms.length + 1}`;

    rooms.push(obj);
  }

  if (rooms.length === 0) {
    return {
      rooms: [],
      errors: ["No data rows found in the file"],
      warnings,
    };
  }

  return { rooms, errors: [], warnings };
}

// Build & download an Excel template with a true Excel dropdown for Unit Model
// (data validation list). ExcelJS is dynamically imported so it's not loaded
// until the user actually clicks "Template".
//
// `unitSystem` = "ip" (default) or "si". When "si", column headers and example
// values are in SI (W, L/s); the parser auto-detects the unit from the header
// and converts back to IP on import.
export async function downloadRoomsTemplate(unitSystem = "ip") {
  const isSI = unitSystem === "si";
  const ExcelJS = (await import("exceljs")).default || (await import("exceljs"));
  const wb = new ExcelJS.Workbook();
  wb.creator = "ZAMBA Product Selector";
  wb.created = new Date();

  const ws = wb.addWorksheet("Rooms");
  const headers = isSI
    ? [
        "Room Name",
        "QTY",
        "Total Cooling (W)",
        "Sensible Cooling (W)",
        "Heating (W)",
        "OA (L/s)",
        "EA (L/s, N/N-f only)",
        "Unit Model",
      ]
    : [
        "Room Name",
        "QTY",
        "Total Cooling (BTU/h)",
        "Sensible Cooling (BTU/h)",
        "Heating (BTU/h)",
        "OA (CFM)",
        "EA (CFM, N/N-f only)",
        "Unit Model",
      ];
  ws.addRow(headers);
  // Header styling
  ws.getRow(1).eachCell((cell) => {
    cell.font = { bold: true };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF1F5F9" } };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border = {
      bottom: { style: "thin", color: { argb: "FF94A3B8" } },
    };
  });

  // Column widths
  ws.columns = [
    { width: 22 }, // Room Name
    { width: 8  }, // QTY
    { width: 22 }, // Total
    { width: 24 }, // Sensible
    { width: 18 }, // Heating
    { width: 12 }, // OA
    { width: 22 }, // EA
    { width: 18 }, // Unit Model
  ];

  // Example rows (capacity in W, airflow in L/s for SI)
  const examples = isSI
    ? [
        // Approx conversions of IP examples: 8000 BTU/h ≈ 2345 W, 50 CFM ≈ 24 L/s
        ["Office 101", 1, 2345, 1905, 1612, 24, 19, "Model N-f"],
        ["Office 102", 2, 3517, 2638, 2345, 38, 33, "Model N-f"],
        ["Open Lounge", 1, 5275, 4103, 3517, 71, 0, "Model S"],
      ]
    : [
        ["Office 101", 1, 8000, 6500, 5500, 50, 40, "Model N-f"],
        ["Office 102", 2, 12000, 9000, 8000, 80, 70, "Model N-f"],
        ["Open Lounge", 1, 18000, 14000, 12000, 150, 0, "Model S"],
      ];
  examples.forEach((r) => ws.addRow(r));

  // Add 47 blank rows so user can fill straight down (50 total)
  for (let i = 0; i < 47; i++) {
    ws.addRow(["", "", "", "", "", "", "", ""]);
  }

  // Data validation: Unit Model dropdown (Column H), rows 2..51
  // ExcelJS supports the `formulae: ['"a,b,c"']` inline-list pattern.
  const lastRow = 1 + examples.length + 47; // 51
  for (let r = 2; r <= lastRow; r++) {
    ws.getCell(`H${r}`).dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: [`"${ALL_MODELS.join(",")}"`],
      showInputMessage: true,
      promptTitle: "Unit Model",
      prompt: "Pick a ZAMBA unit model from the list",
      showErrorMessage: true,
      errorStyle: "stop",
      errorTitle: "Invalid model",
      error: "Please pick a model from the dropdown",
    };
  }

  // Helper "Models" sheet (visible reference)
  const ms = wb.addWorksheet("Models");
  ms.addRow(["Available Unit Models"]);
  ms.getRow(1).font = { bold: true };
  ALL_MODELS.forEach((m) => ms.addRow([m]));
  ms.columns = [{ width: 24 }];

  // Trigger download
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = isSI ? "ZAMBA_Rooms_Template_SI.xlsx" : "ZAMBA_Rooms_Template.xlsx";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }, 100);
}

export const downloadRoomsTemplateSI = () => downloadRoomsTemplate("si");
